import type { ValueFormatterParams } from "ag-grid-enterprise";
import { percentFormatter, percentUnitFormatter } from "./valueFormatters";

export const stringColumnFilters = {
  filter: "agMultiColumnFilter",
  filterParams: {
    filters: [
      {
        filter: "agTextColumnFilter",
        display: "accordion",
        filterParams: { buttons: ["apply", "reset"] }
      },
      {
        filter: "agSetColumnFilter",
        display: "accordion",
        filterParams: { buttons: ["apply", "reset"] }
      }
    ]
  }
} as const;

export const numberColumnFilters = {
  filter: "agMultiColumnFilter",
  filterParams: {
    filters: [
      {
        filter: "agNumberColumnFilter",
        display: "accordion",
        filterParams: { buttons: ["apply", "reset"] }
      },
      {
        filter: "agSetColumnFilter",
        display: "accordion",
        filterParams: { buttons: ["apply", "reset"] }
      }
    ]
  }
} as const;

/** AG Grid's own placeholder with the unit: "Filter (%)", "From (%)", "To (%)". */
function percentFilterPlaceholder({ placeholder }: { placeholder: string }) {
  return `${placeholder.replace(/(\.\.\.|…)$/, "")} (%)`;
}

/**
 * Number-filter params for percent-typed columns: the filter INPUT speaks
 * percent (type 60 to mean 60%), while the filter MODEL stays a fraction
 * (0.60) — matching the cell values, sorting, and anything serialized from
 * the model (e.g. tradex routes filter models to the backend as fractions).
 *
 * NOTE: these (and `percentSetFilterParams`) are functions — they do not
 * survive a JSON deep-clone. Consumers that clone the config re-attach them
 * with `withPercentFilterFns`.
 */
export const percentNumberFilterParams = {
  filterPlaceholder: percentFilterPlaceholder,
  // toFixed + Number strips float noise (60.54 / 100 === 0.6053999999999999).
  numberParser: (text: string | null) =>
    text == null || text === ""
      ? null
      : Number((Number(text) / 100).toFixed(10)),
  // toFixed + Number strips float noise (0.6054 * 100 === 60.540000000000006).
  numberFormatter: (value: number | null) =>
    value == null ? null : String(Number((value * 100).toFixed(10)))
};

/**
 * Fraction decimals matching the 2-decimal percent labels (0.6054 = "60.54%").
 * Percent set-filter values must be rounded to this precision at the source
 * and matched with the same rounding (`SetFilter.precision` on the backend) —
 * otherwise raw fractions that display identically show up as duplicate
 * list entries.
 */
export const PERCENT_FILTER_PRECISION = 4;

/**
 * Set-filter params for percent-typed columns. AG Grid's Set Filter formats
 * its checkbox list with `useFormatterFromColumn = false` — it deliberately
 * ignores the column's valueFormatter — so without its own formatter the list
 * would read "0.6054" next to cells reading "60.54%". Display-only: the
 * filter model still holds the fraction keys.
 */
export const percentSetFilterParams = {
  valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, 2)
};

/**
 * Decimals for `percentUnit` set-filter values. The stored value is already
 * 0-100, so the label decimals *are* the rounding precision — 2, the finest
 * any percentUnit column displays (e.g. aggregated total discount).
 */
export const PERCENT_UNIT_FILTER_PRECISION = 2;

/**
 * Set-filter params for percentUnit columns: label-only, like
 * `percentSetFilterParams`, but without the ×100 — the value is already
 * percent-scaled. Their number sub-filter needs no conversion for the same
 * reason, which is why there's no `percentUnitNumberFilterParams`.
 *
 * Labels use the rounding precision, not the cell's decimals: a coarser label
 * would render two distinct list entries (15.36 and 15.40) identically, which
 * is the duplicate-looking-dropdown problem the rounding exists to remove. So a
 * 0-decimal cell reading "15 %" lists as "15.00 %" — same number, no ambiguity.
 */
export const percentUnitSetFilterParams = {
  valueFormatter: (params: ValueFormatterParams) =>
    percentUnitFormatter(params, PERCENT_UNIT_FILTER_PRECISION)
};

interface MultiFilterConfig {
  filterParams?: {
    filters?: readonly {
      filter: string;
      filterParams?: Record<string, unknown>;
    }[];
  };
}

/** `MultiFilterConfig` with each sub-filter's `filterParams` widened to admit the injected fns. */
type WithWidenedSubFilterParams<T extends MultiFilterConfig> = Omit<
  T,
  "filterParams"
> &
  MultiFilterConfig;

/**
 * Re-attaches the percent filter functions to a multi-filter config that
 * lacks them — because a JSON deep-clone stripped them, or because the config
 * was built outside `getColumnFiltersByType` (playbook): number sub-filters
 * get `percentNumberFilterParams`, set sub-filters `percentSetFilterParams`.
 */
export function withPercentFilterFns<T extends MultiFilterConfig>(
  config: T
): WithWidenedSubFilterParams<T> {
  return withSubFilterParams(config, (filter) =>
    filter === "agNumberColumnFilter"
      ? percentNumberFilterParams
      : filter === "agSetColumnFilter"
        ? percentSetFilterParams
        : undefined
  );
}

/**
 * `withPercentFilterFns` for percentUnit columns. The number input already
 * takes a percent, so it gets only the unit placeholder.
 */
export function withPercentUnitFilterFns<T extends MultiFilterConfig>(
  config: T
): WithWidenedSubFilterParams<T> {
  return withSubFilterParams(config, (filter) =>
    filter === "agNumberColumnFilter"
      ? { filterPlaceholder: percentFilterPlaceholder }
      : filter === "agSetColumnFilter"
        ? percentUnitSetFilterParams
        : undefined
  );
}

function withSubFilterParams<T extends MultiFilterConfig>(
  config: T,
  pick: (filter: string) => Record<string, unknown> | undefined
): WithWidenedSubFilterParams<T> {
  const filters = config.filterParams?.filters;
  if (!filters) {
    return config;
  }
  return {
    ...config,
    filterParams: {
      ...config.filterParams,
      filters: filters.map((sub) => {
        const extra = pick(sub.filter);
        return extra
          ? { ...sub, filterParams: { ...sub.filterParams, ...extra } }
          : sub;
      })
    }
  };
}

// Same shape as numberColumnFilters, with the percent-unit conversion spliced
// into each sub-filter's params — kept in sync automatically since it's
// derived rather than hand-duplicated.
export const percentColumnFilters = withPercentFilterFns(numberColumnFilters);

export const percentUnitColumnFilters =
  withPercentUnitFilterFns(numberColumnFilters);

/**
 * Get filter configuration based on column type.
 * - string: Text Filter + Set Filter
 * - float/integer: Number Filter + Set Filter
 * - percent: Number Filter (percent-unit input) + Set Filter
 * - percentUnit: Number Filter + Set Filter (value is already 0-100, no unit conversion)
 * - array: null (no filters)
 */
export function getColumnFiltersByType(type: string) {
  switch (type) {
    case "string":
      return stringColumnFilters;
    case "float":
      return numberColumnFilters;
    case "integer":
      return numberColumnFilters;
    case "percent":
      return percentColumnFilters;
    case "percentUnit":
      return percentUnitColumnFilters;
    case "array":
      return null;
    case "image":
      return null;
    default:
      return null;
  }
}
